import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';

import { DealRecordType } from '@marxbiotech/shared-types/bff';

import { accountLayoutQueryOptions } from '@lib/query/options/account';
import { mainLayoutQueryOptions } from '@lib/query/options/layout';
import { dealRecordsQueryOptions } from '@lib/query/options/payment';
import { InlineStatusMessage } from '@modules/common/components/inline-status-message';
import { CodRefundBalanceBlock } from '@modules/payment/components/cod-refund-balance-block';
import { DealRecordTable } from '@modules/payment/components/deal-record-table';

// Server-side 預先縮小 payload；不是業務規則過濾器。
// 真正「是否顯示」的判斷在下面用 codRefundDifference 做。
const COD_DEAL_RECORD_TYPES = [
  DealRecordType.COD_REQUEST,
  DealRecordType.COD_REFUND,
  DealRecordType.COD_ADJUST,
];

/**
 * Account COD Refund Balance Page (退貨退款餘額)
 *
 * Displays COD refund balance and transaction history.
 * Only visible to customers with COD payment enabled.
 */
export default function AccountCodRefundBalancePage() {
  const { countryCode } = useParams<{ countryCode: string }>();
  const { data: accountData } = useSuspenseQuery(accountLayoutQueryOptions());
  const { data: mainLayoutData } = useSuspenseQuery(mainLayoutQueryOptions(countryCode!));

  const customer = accountData?.customer;
  const hasPaymentAccount = !!customer?.metadata?.marx_payment_account_id;
  const isCodEnabled = mainLayoutData.paymentCodEnabled;
  const canFetchPayment = hasPaymentAccount && isCodEnabled;

  // Fetch COD deal records (only when user has a payment account and COD is enabled)
  const { data: dealRecords = [], isError: isDealRecordsError } = useQuery({
    ...dealRecordsQueryOptions({
      types: COD_DEAL_RECORD_TYPES,
      limit: 100,
      sortBy: 'dealtAt',
      orderBy: 'DESC',
    }),
    enabled: canFetchPayment,
  });

  // 只保留真正影響「退貨退款餘額」（codRefundBalance）的紀錄。
  // 以欄位為準而非以 type 為準：payment-core 的 COD_REFUND / COD_REQUEST 都可能
  // 帶 codRefundDifference === 0（例如 COD 取消未付款、發起新 COD 訂單等情境），
  // 這些紀錄對 refund balance 無影響，顯示在本頁面只會造成誤導。
  const filteredDealRecords = dealRecords.filter(
    (record) => !!record.codRefundDifference
  );

  if (!customer) {
    return null;
  }

  // Redirect if COD is not enabled or user has no payment account
  if (!isCodEnabled || !hasPaymentAccount) {
    return <Navigate to={`/${countryCode ?? 'tw'}/account`} replace />;
  }

  return (
    <div className="w-full" data-testid="marx-cod-refund-balance-page-wrapper">
      <div className="mb-8 flex items-center gap-y-4">
        <h1 className="text-black-30 text-3xl font-bold">退貨退款餘額</h1>
      </div>
      <CodRefundBalanceBlock />
      {isDealRecordsError ? (
        <InlineStatusMessage>無法取得交易紀錄，請稍後再試</InlineStatusMessage>
      ) : (
        <DealRecordTable
          dealRecords={filteredDealRecords}
          amountField="codRefundDifference"
        />
      )}
    </div>
  );
}
